import { API_URL } from '@/config';
import { formatDateTime } from '@/utils/dateFormat';
import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Check, X, Image as ImageIcon, Video, Eye, Calendar, User, Trash2, Share2, Facebook, Twitter, Linkedin, MessageCircle, ExternalLink } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import axios from 'axios';
import { toast } from 'sonner';
import { getImageUrl } from '@/utils/imageUrl';
import { useOutletContext } from 'react-router-dom';

const API = `${API_URL}/api`;

const MediaModerationPage = () => {
  const { user } = useOutletContext();
  const [media, setMedia] = useState([]);
  const [events, setEvents] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: 'pending', type: 'all', event_id: 'all' });
  const [previewMedia, setPreviewMedia] = useState(null);
  const [deleteMediaId, setDeleteMediaId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const isSuperAdmin = user?.role === 'Super Admin';

  useEffect(() => { 
    loadData(); 
  }, []);

  useEffect(() => {
    loadMedia();
  }, [filters]);

  const loadData = async () => {
    try {
      const [eventsRes, membersRes] = await Promise.all([
        axios.get(`${API}/events`, { headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` } }),
        axios.get(`${API}/members`, { headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` } })
      ]);
      setEvents(eventsRes.data);
      setMembers(membersRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const loadMedia = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status && filters.status !== 'all') params.append('status', filters.status);
      if (filters.type && filters.type !== 'all') params.append('type', filters.type);
      if (filters.event_id && filters.event_id !== 'all') params.append('event_id', filters.event_id);
      
      const res = await axios.get(`${API}/media?${params.toString()}`, { headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` } });
      setMedia(res.data);
    } catch (error) {
      toast.error('Failed to load media');
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (mediaId, status) => {
    try {
      await axios.put(`${API}/media/${mediaId}`, { status }, { headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` } });
      toast.success(`Media ${status}`);
      loadMedia();
    } catch (error) {
      toast.error('Failed to update media');
    }
  };

  const handleDelete = async (mediaId) => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/media/${mediaId}`, { headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` } });
      toast.success('Media deleted successfully');
      setDeleteMediaId(null);
      setPreviewMedia(null);
      loadMedia();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete media');
    } finally {
      setDeleting(false);
    }
  };

  const getUploaderName = (uploaderId) => {
    const member = members.find(m => m.user_id === uploaderId);
    return member?.name || 'Unknown';
  };

  const getEventName = (eventId) => {
    const event = events.find(e => e.event_id === eventId);
    return event?.title || 'No event';
  };

  const formatApprovalDate = (isoString) => {
    if (!isoString) return '';
    return formatDateTime(isoString);
  };

  const handleShare = (platform, item) => {
    const baseUrl = window.location.origin;
    const mediaUrl = getImageUrl(item.url);
    const shareUrl = mediaUrl.startsWith('http') ? mediaUrl : `${baseUrl}${mediaUrl}`;
    const eventName = getEventName(item.event_id);
    const shareText = encodeURIComponent(`${item.caption || 'Media'} - ${eventName} | Lions Club Jaipur Hawamahal`);
    const shareUrlEncoded = encodeURIComponent(shareUrl);
    
    let url = '';
    switch (platform) {
      case 'facebook':
        url = `https://www.facebook.com/sharer/sharer.php?u=${shareUrlEncoded}`;
        break;
      case 'twitter':
        url = `https://twitter.com/intent/tweet?text=${shareText}&url=${shareUrlEncoded}`;
        break;
      case 'linkedin':
        url = `https://www.linkedin.com/sharing/share-offsite/?url=${shareUrlEncoded}`;
        break;
      case 'whatsapp':
        url = `https://api.whatsapp.com/send?text=${shareText}%20${shareUrlEncoded}`;
        break;
      case 'copy':
        navigator.clipboard.writeText(shareUrl);
        toast.success('Link copied to clipboard!');
        return;
      default:
        return;
    }
    
    window.open(url, '_blank', 'width=600,height=400');
  };

  const pendingCount = media.filter(m => m.status === 'pending').length;

  return (
    <div className="space-y-6" data-testid="media-moderation-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold" style={{ color: '#0F172A' }}>Media Moderation</h2>
          <p className="text-muted-foreground">Review and approve member uploads</p>
        </div>
        {pendingCount > 0 && (
          <div className="px-4 py-2 rounded-lg" style={{ backgroundColor: '#FEF3C7' }}>
            <span className="font-semibold" style={{ color: '#92400E' }}>{pendingCount} pending review</span>
          </div>
        )}
      </div>
      
      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <Select value={filters.status} onValueChange={(v) => setFilters({...filters, status: v})}>
          <SelectTrigger className="w-40" data-testid="filter-status">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
        
        <Select value={filters.type} onValueChange={(v) => setFilters({...filters, type: v})}>
          <SelectTrigger className="w-40" data-testid="filter-type">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="photo">Photos</SelectItem>
            <SelectItem value="video">Videos</SelectItem>
          </SelectContent>
        </Select>
        
        <Select value={filters.event_id} onValueChange={(v) => setFilters({...filters, event_id: v})}>
          <SelectTrigger className="w-48" data-testid="filter-event">
            <SelectValue placeholder="All Events" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Events</SelectItem>
            {events.map(e => <SelectItem key={e.event_id} value={e.event_id}>{e.title}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin h-12 w-12 border-b-2 border-primary rounded-full"></div>
        </div>
      ) : media.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <ImageIcon className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No media found matching your filters</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {media.map((item) => (
            <Card key={item.media_id} className="overflow-hidden" data-testid={`media-item-${item.media_id}`}>
              <div 
                className="aspect-square overflow-hidden cursor-pointer relative group"
                onClick={() => setPreviewMedia(item)}
              >
                {item.type === 'photo' ? (
                  <img 
                    src={getImageUrl(item.url)} 
                    alt={item.caption || 'Media'} 
                    className="w-full h-full object-cover transition-transform group-hover:scale-105" 
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)' }}>
                    <Video className="w-16 h-16 text-white" />
                  </div>
                )}
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <Eye className="w-8 h-8 text-white" />
                </div>
              </div>
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground mb-2 line-clamp-2">{item.caption || 'No caption'}</p>
                <p className="text-xs text-muted-foreground mb-1">By: {getUploaderName(item.uploader_member_id)}</p>
                <p className="text-xs text-muted-foreground mb-3">Event: {getEventName(item.event_id)}</p>
                
                {/* Approval Details */}
                {item.status === 'approved' && item.approved_by_name && (
                  <div className="mb-3 p-2 rounded-md bg-green-50 border border-green-200">
                    <div className="flex items-center gap-1 text-xs text-green-700">
                      <User className="w-3 h-3" />
                      <span>Approved by: <strong>{item.approved_by_name}</strong></span>
                    </div>
                    {item.approved_at && (
                      <div className="flex items-center gap-1 text-xs text-green-600 mt-1">
                        <Calendar className="w-3 h-3" />
                        <span>{formatApprovalDate(item.approved_at)}</span>
                      </div>
                    )}
                  </div>
                )}
                
                <div className="flex items-center justify-between">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    item.status === 'approved' ? 'bg-green-100 text-green-800' : 
                    item.status === 'rejected' ? 'bg-red-100 text-red-800' : 
                    'bg-yellow-100 text-yellow-800'
                  }`}>
                    {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                  </span>
                  <div className="flex gap-2">
                    {item.status === 'approved' && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="h-8 w-8 p-0"
                            onClick={(e) => e.stopPropagation()}
                            title="Share to social media"
                          >
                            <Share2 className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleShare('facebook', item)}>
                            <Facebook className="w-4 h-4 mr-2 text-blue-600" />
                            Share on Facebook
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleShare('twitter', item)}>
                            <Twitter className="w-4 h-4 mr-2 text-sky-500" />
                            Share on Twitter/X
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleShare('linkedin', item)}>
                            <Linkedin className="w-4 h-4 mr-2 text-blue-700" />
                            Share on LinkedIn
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleShare('whatsapp', item)}>
                            <MessageCircle className="w-4 h-4 mr-2 text-green-600" />
                            Share on WhatsApp
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleShare('copy', item)}>
                            <ExternalLink className="w-4 h-4 mr-2" />
                            Copy Link
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                    {item.status === 'pending' && (
                      <>
                        <Button 
                          size="sm" 
                          className="h-8 w-8 p-0"
                          style={{ backgroundColor: '#22C55E' }}
                          onClick={(e) => { e.stopPropagation(); handleAction(item.media_id, 'approved'); }} 
                          data-testid={`approve-${item.media_id}`}
                        >
                          <Check className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="destructive"
                          className="h-8 w-8 p-0"
                          onClick={(e) => { e.stopPropagation(); handleAction(item.media_id, 'rejected'); }} 
                          data-testid={`reject-${item.media_id}`}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                    {isSuperAdmin && (
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="h-8 w-8 p-0 border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                        onClick={(e) => { e.stopPropagation(); setDeleteMediaId(item.media_id); }} 
                        data-testid={`delete-${item.media_id}`}
                        title="Delete media"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      
      {/* Preview Dialog */}
      <Dialog open={!!previewMedia} onOpenChange={() => setPreviewMedia(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{previewMedia?.caption || 'Media Preview'}</DialogTitle>
          </DialogHeader>
          {previewMedia && (
            <div className="space-y-4">
              {previewMedia.type === 'photo' ? (
                <img 
                  src={getImageUrl(previewMedia.url)}
                  alt={previewMedia.caption || 'Media'} 
                  className="w-full rounded-lg" 
                />
              ) : (
                <video 
                  src={getImageUrl(previewMedia.url)}
                  controls 
                  className="w-full rounded-lg"
                />
              )}
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-sm">Uploaded by: <strong>{getUploaderName(previewMedia.uploader_member_id)}</strong></p>
                  <p className="text-sm text-muted-foreground">Event: {getEventName(previewMedia.event_id)}</p>
                  {previewMedia.status === 'approved' && previewMedia.approved_by_name && (
                    <div className="mt-2 p-2 rounded-md bg-green-50 border border-green-200">
                      <p className="text-sm text-green-700">
                        <User className="w-4 h-4 inline mr-1" />
                        Approved by: <strong>{previewMedia.approved_by_name}</strong>
                      </p>
                      {previewMedia.approved_at && (
                        <p className="text-sm text-green-600 mt-1">
                          <Calendar className="w-4 h-4 inline mr-1" />
                          {formatApprovalDate(previewMedia.approved_at)}
                        </p>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex gap-2 flex-wrap justify-end">
                  {previewMedia.status === 'pending' && (
                    <>
                      <Button 
                        style={{ backgroundColor: '#22C55E' }}
                        onClick={() => { handleAction(previewMedia.media_id, 'approved'); setPreviewMedia(null); }}
                      >
                        <Check className="w-4 h-4 mr-2" /> Approve
                      </Button>
                      <Button 
                        variant="destructive"
                        onClick={() => { handleAction(previewMedia.media_id, 'rejected'); setPreviewMedia(null); }}
                      >
                        <X className="w-4 h-4 mr-2" /> Reject
                      </Button>
                    </>
                  )}
                  {isSuperAdmin && (
                    <Button 
                      variant="outline"
                      className="border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                      onClick={() => setDeleteMediaId(previewMedia.media_id)}
                    >
                      <Trash2 className="w-4 h-4 mr-2" /> Delete
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteMediaId} onOpenChange={() => setDeleteMediaId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Media</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to permanently delete this media? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => handleDelete(deleteMediaId)}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default MediaModerationPage;
